import {
  LayoutDashboard,
  Package,
  Users,
  ClipboardList,
  AlertCircle,
  CreditCard,
  Receipt,
  Calendar,
  Award,
  Truck,
  Cpu,
  Wrench,
  Building,
  Building2,
  Bell,
  ShieldAlert,
  Database,
  FileText,
  RotateCcw,
  RefreshCw,
  UserCheck,
  DollarSign,
  Upload,
} from 'lucide-react'
import { MenuItem } from '@/types/menu'

/**
 * 菜单配置数据
 *
 * 注意：
 * - 所有菜单项必须有唯一的 id
 * - roles 数组定义了哪些角色可以访问该菜单
 * - children 用于定义二级菜单（递归结构）
 * - badge: 'notification' 表示在菜单项上显示通知未读数
 *
 * 菜单结构重构说明：
 * - "工作台首页"已改名为"工作台"，作为所有角色的统一入口
 * - 移除了"市场部工作台"、"客服部工作台"、"财务工作台"、"工程师工作台"菜单
 * - 原子菜单（异常处理、证书管理、快递追踪、实验室检测、支付管理、发票管理、月结管理）提升为一级菜单
 * - 管理员通过顶部工具栏角色切换器查看不同角色的工作台
 */
export const menuConfig: MenuItem[] = [
  { id: 'task-voucher-assist', title: '付款协助', href: '/dashboard/tasks/voucher-assist', icon: ClipboardList, roles: ['ADMIN', 'MARKET'] },
  { id: 'task-receipt-register', title: '到账登记', href: '/dashboard/tasks/receipt-register', icon: ClipboardList, roles: ['ADMIN', 'FINANCE'] },
  { id: 'market-workflow-records', title: '申请与审批管理', href: '/dashboard/market/workflow-records', icon: ClipboardList, roles: ['ADMIN', 'MARKET'] },
  { id: 'market-payment-assistance', title: '付款协助管理', href: '/dashboard/market/payment-assistance', icon: ClipboardList, roles: ['ADMIN', 'MARKET'] },
  { id: 'engineer-records', title: '检测记录管理', href: '/dashboard/engineer/records', icon: ClipboardList, roles: ['ADMIN', 'ENGINEER', 'LAB_ENGINEER'] },
  { id: 'task-customer', taskBadge: 'customerPending', title: '我的待办', href: '/dashboard/tasks/customer', icon: ClipboardList, roles: ['CUSTOMER'] },
  // 通用工作台/首页：仅管理员与客户（无多角色歧义）。业务角色改用各自的「xxx 任务」菜单。
  {
    id: 'dashboard',
    title: '工作台',
    href: '/dashboard',
    icon: LayoutDashboard,
    // 统一任务工作台是所有员工的落地页；workbenchTotal 角标显示本人待办总数（>0 才显示）
    roles: ['ADMIN', 'MARKET', 'SERVICE', 'ENGINEER', 'LAB_ENGINEER', 'FINANCE', 'MONTHLY_ADMIN', 'CUSTOMER'],
    taskBadge: 'workbenchTotal',
  },
  // 业务流·任务步骤页（每步一个聚焦处理页，只列待办+单一动作；工作台点任务跳此）
  // 注：旧的「检测/客服/市场/财务任务」角色聚合页已拆解并入下列细粒度任务页 + 订单管理。
  {
    id: 'onsite-orders',
    title: '新建订单',
    href: '/dashboard/onsite-orders',
    icon: FileText,
    roles: ['ADMIN', 'MARKET', 'SERVICE', 'ENGINEER'],
  },
  {
    id: 'task-assign',
    title: '分配',
    href: '/dashboard/tasks/assign',
    icon: Package,
    roles: ['ADMIN', 'MARKET'],
    taskBadge: 'marketPendingAssign',
  },
  {
    id: 'task-invoice-approval',
    title: '开票申请与核价',
    href: '/dashboard/tasks/invoice-approval',
    icon: FileText,
    roles: ['ADMIN', 'MARKET'],
    taskBadge: 'invoiceMarketWork',
  },
  {
    id: 'task-payment-claim',
    title: '回款认领',
    href: '/dashboard/tasks/payment-claim',
    icon: DollarSign,
    roles: ['ADMIN', 'MARKET'],
    taskBadge: 'paymentClaimReview',
  },
  {
    id: 'task-issue-invoice',
    title: '开票',
    href: '/dashboard/tasks/issue-invoice',
    icon: Receipt,
    roles: ['ADMIN', 'FINANCE'],
    taskBadge: 'financePendingInvoice',
  },
  {
    id: 'task-voucher-audit',
    title: '凭证审核',
    href: '/dashboard/tasks/voucher-audit',
    icon: CreditCard,
    roles: ['ADMIN', 'FINANCE'],
    taskBadge: 'financePendingVoucher',
  },
  {
    id: 'task-payment-reconcile',
    title: '回款核销',
    href: '/dashboard/tasks/payment-reconcile',
    icon: CreditCard,
    roles: ['ADMIN', 'FINANCE'],
    taskBadge: 'financeSettlementWork',
  },
  {
    id: 'task-dispatch',
    title: '上门派单',
    href: '/dashboard/tasks/dispatch',
    icon: ClipboardList,
    roles: ['ADMIN', 'SERVICE'],
    taskBadge: 'onsitePendingDispatch',
  },
  {
    id: 'task-outsource-testing', title: '代送检测跟进', href: '/dashboard/tasks/outsource-testing', icon: Package, roles: ['ADMIN', 'SERVICE'], taskBadge: 'outsourceTestingPending',
  },
  {
    id: 'task-mail-receive',
    title: '邮寄 / 代送收件',
    href: '/dashboard/tasks/mail-receive',
    icon: Package,
    roles: ['ADMIN', 'SERVICE'],
    taskBadge: 'mailReceivePending',
  },
  {
    id: 'task-mail-dispatch',
    title: '邮寄派单',
    href: '/dashboard/tasks/mail-dispatch',
    icon: ClipboardList,
    roles: ['ADMIN', 'SERVICE'],
    taskBadge: 'mailDispatchPending',
  },
  {
    id: 'task-mail-return',
    title: '邮寄 / 代送寄回',
    href: '/dashboard/tasks/mail-return',
    icon: Truck,
    roles: ['ADMIN', 'SERVICE'],
    taskBadge: 'mailReturnPending',
  },
  {
    id: 'task-inspect-onsite',
    title: '上门检测',
    href: '/dashboard/tasks/inspect-onsite',
    icon: Wrench,
    roles: ['ADMIN', 'ENGINEER'],
    taskBadge: 'engineerScheduledOnsite',
  },
  {
    id: 'task-inspect-mail',
    title: '邮寄检测',
    href: '/dashboard/tasks/inspect-mail',
    icon: Wrench,
    roles: ['ADMIN', 'LAB_ENGINEER'],
    taskBadge: 'engineerScheduledMail',
  },
  {
    id: 'task-cert',
    title: '证书上传',
    href: '/dashboard/tasks/cert',
    icon: Award,
    roles: ['ADMIN', 'SERVICE'],
    taskBadge: 'certPending',
  },
  {
    id: 'task-refund-approval',
    title: '退款审批',
    href: '/dashboard/tasks/refund-approval',
    icon: RotateCcw,
    roles: ['ADMIN', 'MARKET'],
    taskBadge: 'refundMarketReview',
  },
  {
    id: 'task-reissue-approval',
    title: '换票审批',
    href: '/dashboard/tasks/reissue-approval',
    icon: RefreshCw,
    roles: ['ADMIN', 'MARKET'],
    taskBadge: 'reissueMarketReview',
  },
  {
    id: 'task-refund',
    taskBadge: 'financePendingRefund',
    title: '退款',
    href: '/dashboard/tasks/refund',
    icon: RotateCcw,
    roles: ['ADMIN', 'FINANCE'],
  },
  {
    id: 'task-exception',
    title: '异常处理',
    href: '/dashboard/tasks/exception',
    icon: AlertCircle,
    roles: ['ADMIN', 'MARKET'],
    taskBadge: 'exceptionPending',
  },
  {
    id: 'task-monthly',
    title: '月结审核',
    href: '/dashboard/tasks/monthly',
    icon: Calendar,
    roles: ['ADMIN', 'MONTHLY_ADMIN'],
    taskBadge: 'monthlyPendingAudit',
  },
  {
    id: 'task-records',
    title: '处理记录',
    href: '/dashboard/tasks/records',
    icon: FileText,
    roles: ['ADMIN', 'MARKET', 'SERVICE', 'ENGINEER', 'LAB_ENGINEER', 'FINANCE', 'MONTHLY_ADMIN'],
  },
  // 客户专属菜单
  {
    id: 'my-orders',
    title: '我的订单',
    href: '/dashboard/my-orders',
    icon: Package,
    roles: ['CUSTOMER'],
  },
  {
    id: 'my-payments',
    title: '我的支付',
    href: '/dashboard/my-payments',
    icon: CreditCard,
    roles: ['CUSTOMER'],
  },
  {
    id: 'my-invoices',
    title: '我的发票',
    href: '/dashboard/my-invoices',
    icon: Receipt,
    roles: ['CUSTOMER'],
  },
  {
    id: 'my-certificates',
    title: '我的证书',
    href: '/dashboard/my-certificates',
    icon: Award,
    roles: ['CUSTOMER'],
  },
  {
    id: 'my-company',
    title: '我的公司',
    href: '/dashboard/my-company',
    icon: Building,
    roles: ['CUSTOMER'],
  },
  {
    id: 'my-refunds',
    title: '我的退款',
    href: '/dashboard/my-refunds',
    icon: RotateCcw,
    roles: ['CUSTOMER'],
  },
  // 仅月结管理员客户可见
  {
    id: 'my-monthly',
    title: '月结管理',
    href: '/dashboard/my-monthly',
    icon: Calendar,
    roles: ['CUSTOMER'],
    condition: (ctx) => ctx.isMonthlyAdmin === true,
  },
  {
    id: 'my-profile',
    title: '个人中心',
    href: '/dashboard/my-profile',
    icon: UserCheck,
    roles: ['CUSTOMER'],
  },
  // 管理员菜单
  {
    id: 'orders',
    title: '订单管理',
    href: '/dashboard/orders',
    icon: Package,
    roles: ['ADMIN', 'MARKET', 'FINANCE'],
    taskBadge: 'marketPendingAssign',
  },
  {
    id: 'assisted-order-onsite',
    title: '代客录单·上门',
    href: '/dashboard/orders/assisted/onsite/new',
    icon: UserCheck,
    roles: ['ADMIN', 'MARKET', 'SERVICE'],
  },
  {
    id: 'assisted-order-outsource', title: '代客录单·代送', href: '/dashboard/orders/assisted/outsource/new', icon: Package, roles: ['ADMIN', 'MARKET', 'SERVICE'],
  },
  {
    id: 'assisted-order-mail',
    title: '代客录单·邮寄',
    href: '/dashboard/orders/assisted/mail/new',
    icon: Package,
    roles: ['ADMIN', 'MARKET', 'SERVICE'],
  },
  {
    id: 'order-public-pool',
    title: '订单公海',
    href: '/dashboard/orders/public-pool',
    icon: Database,
    roles: ['MARKET'],
  },
  // 代客批量导入订单（管理组，非化身）：市场/客服拿客户 Excel 台账一次建多单
  {
    id: 'orders-batch-import',
    title: '批量导入订单',
    href: '/dashboard/orders/batch-import',
    icon: Upload,
    roles: ['ADMIN', 'MARKET', 'SERVICE'],
  },
  // 原市场部工作台子菜单，提升为一级菜单
  {
    id: 'exceptions',
    title: '异常管理',
    href: '/dashboard/market/exceptions',
    icon: AlertCircle,
    roles: ['ADMIN', 'MARKET'],
  },
  {
    id: 'dispatch-overview',
    title: '上门检测管理',
    href: '/dashboard/service/dispatch-overview',
    icon: ClipboardList,
    roles: ['ADMIN', 'SERVICE', 'MARKET'],
    taskBadge: 'onsitePendingDispatch',
  },
  {
    id: 'schedule-board',
    title: '行程视图',
    href: '/dashboard/service/schedule',
    icon: Calendar,
    roles: ['ADMIN', 'SERVICE', 'MARKET'],
  },
  {
    id: 'outsource-workbench', title: '代送检测管理', href: '/dashboard/service/outsource-workbench', icon: Package, roles: ['ADMIN', 'MARKET', 'SERVICE'],
  },
  {
    id: 'mail-workbench',
    title: '邮寄检测管理',
    href: '/dashboard/service/mail-workbench',
    icon: Truck,
    roles: ['ADMIN', 'SERVICE', 'MARKET'],
    taskBadge: 'mailPending',
  },
  {
    id: 'inspection-records',
    title: '检测数据复核',
    href: '/dashboard/service/inspection-records',
    icon: UserCheck,
    roles: ['ADMIN', 'SERVICE'],
  },
  // 退检/取消台账（NEW-E）：跨期检索设备级退检/重派/取消留痕
  {
    id: 'return-ledger',
    title: '退检台账',
    href: '/dashboard/return-ledger',
    icon: RotateCcw,
    roles: ['ADMIN', 'MARKET', 'SERVICE'],
  },
  // 原客服部工作台子菜单，提升为一级菜单
  {
    id: 'certificates',
    title: '证书管理',
    href: '/dashboard/service/certificates',
    icon: Award,
    roles: ['ADMIN', 'SERVICE'],
    taskBadge: 'certPending',
  },
  // 原财务工作台子菜单，提升为一级菜单
  {
    id: 'finance-orders',
    title: '订单单据',
    href: '/dashboard/finance/orders',
    icon: FileText,
    roles: ['ADMIN', 'FINANCE'],
  },
  {
    id: 'payments',
    title: '支付管理',
    href: '/dashboard/finance/payments',
    icon: CreditCard,
    roles: ['ADMIN', 'FINANCE'],
    taskBadge: 'financePendingVoucher',
  },
  {
    id: 'invoices',
    title: '发票管理',
    href: '/dashboard/finance/invoices',
    icon: Receipt,
    roles: ['ADMIN', 'FINANCE'],
    taskBadge: 'financePendingInvoice',
  },
  {
    id: 'cost-profit',
    title: '成本毛利',
    href: '/dashboard/finance/cost-profit',
    icon: DollarSign,
    roles: ['ADMIN', 'FINANCE'],
  },
  {
    id: 'prepayments',
    title: '预收账款',
    href: '/dashboard/finance/prepayments',
    icon: CreditCard,
    roles: ['ADMIN', 'FINANCE'],
  },
  {
    id: 'refunds',
    title: '退款管理',
    href: '/dashboard/finance/refunds',
    icon: RotateCcw,
    roles: ['ADMIN', 'FINANCE'],
  },
  {
    id: 'reissue-pending',
    title: '换票重开',
    href: '/dashboard/finance/reissue-pending',
    icon: RefreshCw,
    roles: ['ADMIN', 'FINANCE'],
  },
  {
    id: 'monthly',
    title: '月结管理',
    href: '/dashboard/finance/monthly',
    icon: Calendar,
    roles: ['ADMIN', 'FINANCE'],
  },
  // 月结管理员专用工作台保留
  {
    id: 'monthly-company',
    title: '公司月结工作台',
    // 纯目录型菜单，无 href
    icon: Building,
    roles: ['MONTHLY_ADMIN'],
    children: [
      {
        id: 'monthly-company-orders',
        title: '公司订单',
        href: '/dashboard/monthly-company/orders',
        icon: Package,
        roles: ['MONTHLY_ADMIN'],
        taskBadge: 'monthlyPendingAudit',
      },
      {
        id: 'monthly-company-info',
        title: '公司信息',
        href: '/dashboard/monthly-company/info',
        icon: Building,
        roles: ['MONTHLY_ADMIN'],
      },
    ],
  },
  // 原「价目配置」目录已拆平为一级菜单（服务项目/零配件页面已随价目 v2 废弃删除）
  {
    id: 'config-device-types',
    title: '设备类型',
    href: '/dashboard/config/device-types',
    icon: Cpu,
    roles: ['ADMIN'],
  },
  {
    id: 'config-inspection-templates',
    title: '检测模板',
    href: '/dashboard/config/inspection-templates',
    icon: FileText,
    roles: ['ADMIN'],
  },
  {
    id: 'config-additional-fees',
    title: '附加费用',
    href: '/dashboard/config/additional-fees',
    icon: DollarSign,
    roles: ['ADMIN'],
  },
  {
    id: 'staff',
    title: '员工管理',
    href: '/dashboard/config/staff',
    icon: Users,
    roles: ['ADMIN'],
  },
  {
    id: 'customer',
    title: '客户管理',
    // 纯目录型菜单，无 href
    icon: Building,
    roles: ['ADMIN'],
    children: [
      {
        id: 'customer-users',
        title: '客户用户',
        href: '/dashboard/customer/users',
        icon: Users,
        roles: ['ADMIN'],
      },
      {
        id: 'customer-companies',
        title: '委托公司',
        href: '/dashboard/customer/companies',
        icon: Building,
        roles: ['ADMIN'],
      },
      {
        id: 'customer-centers',
        title: '中心管理',
        href: '/dashboard/customer/centers',
        icon: Building2,
        roles: ['ADMIN'],
      },
    ],
  },
  {
    id: 'base-data',
    title: '基础数据',
    // 纯目录型菜单，无 href
    icon: Database,
    roles: ['ADMIN'],
    children: [
      {
        id: 'base-data-suppliers',
        title: '供应商管理',
        href: '/dashboard/config/suppliers',
        icon: Truck,
        roles: ['ADMIN'],
      },
    ],
  },
  {
    id: 'notifications',
    title: '通知中心',
    href: '/dashboard/notifications',
    icon: Bell,
    roles: ['ADMIN', 'MARKET', 'SERVICE', 'ENGINEER', 'LAB_ENGINEER', 'FINANCE', 'MONTHLY_ADMIN'],
    badge: 'notification', // 显示未读通知数
  },
  {
    id: 'audit-logs',
    title: '审计日志',
    href: '/dashboard/audit-logs',
    icon: ShieldAlert,
    roles: ['ADMIN'],
  },
  {
    id: 'impersonation-history',
    title: '化身审计',
    href: '/dashboard/impersonation/history',
    icon: UserCheck,
    roles: ['ADMIN'],
  },
]
